import { computed, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, of, timer } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { ProgressBarMode } from '@angular/material/progress-bar';

import { LoadingPageService } from 'src/app/infrastructure/services/loading-services/loading-page.service';
import { LogService } from 'src/app/infrastructure/services/log.service';
import { RequestLoadingService } from 'src/app/infrastructure/services/loading-services/request-loading.service';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';

@Injectable({
    providedIn: 'root'
})
export class ProgressBarService {
    private readonly __loadingPageService = injectRequired(LoadingPageService);
    private readonly __requestLoadingService = injectRequired(RequestLoadingService);

    // Pas tonen als er langer dan __delay iets loopt: snelle acties geven zo geen flits, trage krijgen
    // binnen dit interval zichtbare feedback.
    private readonly __delay = 1000;
    // Pas verbergen als er __hideGrace lang niets meer loopt: overbrugt het korte gat tussen
    // opeenvolgende stappen (bv. save-verzoek → navigatie → resolver → page-load), zodat de balk niet
    // uit- en weer aanknippert en de show-delay niet opnieuw begint.
    private readonly __hideGrace = 150;

    // Zichtbaarheid centraal (root) i.p.v. per ProgressBarComponent: elke pagina heeft een eigen footer
    // en dus een eigen balk-instantie. Met de timing in de component begon een nieuwe pagina telkens
    // weer onzichtbaar (+ opnieuw de show-delay), waardoor de balk bij elke navigatie wegviel en
    // opnieuw startte. Nu neemt een nieuwe instantie meteen de lopende stand over.
    private readonly __visible$$ = signal(false);
    readonly visible$$ = this.__visible$$.asReadonly();

    // Zichtbare laad-spinners van lijsten/grids die nog leeg zijn (zie spinner_set). Zo'n spinner
    // toont al wáár de gebruiker op wacht; de balk is dan dubbelop en blijft weg. Set op eigenaar
    // (de component): aan-/afmelden is idempotent en een destroy ruimt alleen de eigen melding op.
    private readonly __spinnerOwners$$ = signal<ReadonlySet<object>>(new Set());
    private readonly __spinnerVisible$ = toObservable(computed(() => this.__spinnerOwners$$().size > 0));
    // private loadingSubscr: Subscription;

    // public loading: boolean;
    private __mode$ = new BehaviorSubject<ProgressBarMode>('indeterminate');
    mode$ = this.__mode$.asObservable();
    get Mode() {return this.__mode$.value;}
    set Mode(value) {
        this.__mode$.next(value);
    }

    private __progressValue$ = new BehaviorSubject<number>(0);
    progressValue$ = this.__progressValue$.asObservable();
    get ProgressValue() {
        return this.__progressValue$.value;
    }
    set ProgressValue(value) {
        this.__progressValue$.next(value);
    }

    // Expliciete zichtbaarheid los van de page-loading: taken (bv. foto-upload/-delete) kunnen de
    // balk tonen zonder de pagina als 'laden' te markeren (die verbergt de content).
    private __active$ = new BehaviorSubject<boolean>(false);
    active$ = this.__active$.asObservable();
    get Active() {return this.__active$.value;}
    set Active(value: boolean) {
        this.__active$.next(value);
    }
    // public progressCalls = 0;

    constructor() {
        // Zichtbaar bij page-loading, een actieve taak (page-/screen-lock, bv. foto-upload), een lopend
        // server-verzoek of een router-navigatie (RequestLoadingService). Een zichtbare laad-spinner
        // (spinner_set) onderdrukt de balk voor laden/verzoeken, maar niet voor een actieve taak: daar
        // ís de balk de voortgangsindicator. Root-service: leeft zolang de app, dus geen unsubscribe nodig.
        combineLatest([
            this.__loadingPageService.isLoading$,
            this.active$,
            this.__requestLoadingService.busy$,
            this.__spinnerVisible$,
        ]).pipe(
            map(([loading, active, busy, spinner]) => active || ((loading || busy) && !spinner)),
            distinctUntilChanged(),
            switchMap(busy => busy
                ? (this.__visible$$() ? of(true) : timer(this.__delay).pipe(map(() => true)))
                : timer(this.__hideGrace).pipe(map(() => false))
            ),
            distinctUntilChanged(),
        ).subscribe(visible => {
            this.__visible$$.set(visible);
            LogService.envDev.logString('progressBar', visible ? 'start' : 'end');
        });
    }

    // Meldt een in beeld staande laad-spinner aan (visible = true) of af. Alleen gebruiken voor een
    // spinner die een leeg vlak vult tijdens het laden — niet voor een busy-overlay over bestaande
    // inhoud. Afmelden bij destroy is verplicht (DestroyRef/ngOnDestroy), anders blijft de balk weg.
    spinner_set(owner: object, visible: boolean): void {
        const __owners = this.__spinnerOwners$$();
        if (__owners.has(owner) === visible) return;

        const __next = new Set(__owners);
        if (visible) __next.add(owner); else __next.delete(owner);
        this.__spinnerOwners$$.set(__next);
    }

    // constructor(
    //     private loadingService: LoadingService
    // ) {
    //     this.loadingSubscr = this.loadingService.loadingCalls$.subscribe(loadingCalls => {
    //         this.loading = loading;
    //     })
    // }

    // callLoading() {
    //     if (this.progressCalls === 0) {
    //         this.showProgressBar();
    //     }
    //     this.progressCalls++;
    // }

    // dropLoadingCall() {
    //     if (this.progressCalls > 0) {
    //         this.progressCalls--;
    //     }
    //     if (this.progressCalls === 0) {
    //         this.clearLoading();
    //     }
    // }

    // showProgressBar() {
    //     setTimeout(() => this.clearLoading(), 20000);
    // }

    // clearLoading() {
    //     this.progressCalls = 0;
    //     this.loading$.next(false);
    // }

    // ngOnDestroy(): void {
    //     this.loadingSubscr?.unsubscribe();
    // }
}
